/**
 * LearnBoardConnected — the learn agent's face wired to the live training
 * surface, for the Home detail pane (bound to a session, or session-less in
 * FACE mode) and for the apps/learn satellite. Wrapper-as-adapter, the mentor
 * board's precedent: it fetches the learner's own skills and the published
 * catalogue and FOLDS them into the board's view-model.
 *
 * Data (all self-scoped — this is the nurse's OWN learning, not a unit view;
 * the ward-level surface is the mentor agent):
 *   GET /me/skills                   → her fold; each skill carries its target
 *   GET /skills                      → the catalogue that titles each skill
 *   GET /protocols?status=published  → the modules her targets name
 *
 * `/learning-goals` is gone (410). What she is working toward is a TARGET on
 * one skill, and it comes back on the fold rather than as a resource of its
 * own — so the targets here are `flattenTargets(fold.items)`, and whether one
 * is reached is the `state` the fold derived, never a comparison made here.
 *
 * Nothing here is fabricated: a recommendation's REASON is a real open target.
 *
 * Chat side-channel: a plain conversational session (the learn agent's
 * manifest has no command post-processor — its tools run server-side and the board just
 * re-reads afterwards). The composer works session-less too: the first send
 * mints the session via useDeferredFirstSend, then onSessionCreated lets the
 * caller swap into the real conversation.
 */

import { useEffect, useMemo, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useQueryClient } from '@tanstack/react-query'

import type { SessionResponse } from '@/api/types_session'
import type { SessionDetail } from '@/hooks/useSessions'
import { BoardAgentChat, ChatInput } from '@/components/chat'
import { useStreamingMessage } from '@/hooks/useStreamingChat'
import { useDeferredFirstSend } from '@/hooks/useDeferredFirstSend'
import { sessionKeys } from '@/hooks/useSessions'
import { trainingKeys, useTrainingComponents } from '@/hooks/useTraining'
import {
  OWN_SKILLS_KEY,
  flattenTargets,
  sortTargets,
  useOwnSkills,
  useSkillCatalogue,
} from '@frontend/hooks/useSkills'
import { catalogueTitleLookup } from '@frontend/lib/skillTitle'
import { todayCivilDate } from '@frontend/utils/dates'

import { AGENT_SLUG } from '../../agent'
import { LearnBoard } from './LearnBoard'
import { LearnAgentActions } from './LearnAgentActions'
import { foldRecommendations } from './foldLearning'

export interface LearnBoardConnectedProps {
  /** Present on the Session page / detail pane; absent on the session-less
   *  Home face (board + a session-less composer, see useDeferredFirstSend). */
  session?: SessionDetail
  /** Face mode only. Learning is personal-scope — no patient frame — so this
   *  is always undefined in practice; present for AgentDetailViewProps parity. */
  facePatientId?: string | null
  /** Face mode only — fires once the composer's first send mints the session. */
  onSessionCreated?: (session: SessionResponse) => void
}

export function LearnBoardConnected({ session, onSessionCreated }: LearnBoardConnectedProps) {
  const { t, i18n } = useTranslation()
  const queryClient = useQueryClient()

  const skills = useOwnSkills()
  // The tenant catalogue, because the fold names a skill by id and carries no
  // title: a title has a locale and the reader picks it. Without this the board
  // renders `advanced-life-support` where `Erweiterte Reanimation` belongs.
  // Cached for the session (it is a file in the customer's repository), so this
  // is one request per session, not per render.
  const catalogue = useSkillCatalogue()
  const components = useTrainingComponents({ status: 'published', composesComponents: false })

  // --- View-model fold ----------------------------------------------------
  // Every target on the fold, open and closed alike: a target she reached is
  // part of the picture, and hiding it would make the board look like she is
  // only ever behind. `state` is what tells them apart, and it comes on the
  // wire — sortTargets puts the open ones first.
  const skillTitle = useMemo(
    () => catalogueTitleLookup(catalogue.data?.items ?? [], i18n.language),
    [catalogue.data, i18n.language],
  )
  const targets = useMemo(
    () => flattenTargets(skills.data?.items ?? [], skillTitle).sort(sortTargets),
    [skills.data, skillTitle],
  )
  const openTargets = useMemo(() => targets.filter((t) => t.target.state === 'open'), [targets])

  const recommended = useMemo(
    () => foldRecommendations({ components: components.data ?? [], openTargets }),
    [components.data, openTargets],
  )

  // --- Chat side-channel --------------------------------------------------
  const streaming = useStreamingMessage(session?.session.id ?? '')
  const deferred = useDeferredFirstSend({
    sessionId: session?.session.id,
    agentSlug: AGENT_SLUG,
    sendMessage: streaming.sendMessage,
    onCreated: (newSession) => onSessionCreated?.(newSession),
  })

  // A finished turn may have generated a practice quiz or set a target
  // through the agent's tools (executed server-side) —
  // re-read the board once streaming ends, so the surface never lags the
  // conversation that just changed it.
  const wasStreaming = useRef(false)
  useEffect(() => {
    if (streaming.isStreaming) {
      wasStreaming.current = true
    } else if (wasStreaming.current) {
      wasStreaming.current = false
      void queryClient.invalidateQueries({ queryKey: trainingKeys.all })
      void queryClient.invalidateQueries({ queryKey: OWN_SKILLS_KEY })
      if (session?.session.id) {
        void queryClient.invalidateQueries({ queryKey: sessionKeys.detail(session.session.id) })
      }
    }
  }, [streaming.isStreaming, session?.session.id, queryClient])

  return (
    <div className="flex h-full flex-col bg-white">
      <div className="min-h-0 flex-1 overflow-auto" data-testid="learn-board-scroll">
        <LearnBoard
          targets={targets}
          today={todayCivilDate()}
          recommended={recommended}
          targetsLoading={skills.isLoading}
          targetsError={skills.isError}
          onRetryTargets={() => void skills.refetch()}
          // The list needs BOTH reads: the targets name the modules, the
          // catalogue says which of them are published.
          recommendedLoading={skills.isLoading || components.isLoading}
          recommendedError={skills.isError || components.isError}
          onRetryRecommended={() => {
            void skills.refetch()
            void components.refetch()
          }}
        />
      </div>

      {session && (
        <BoardAgentChat
          session={session}
          streaming={streaming}
          className="max-h-[35%] shrink-0 overflow-y-auto border-t border-neutral-200 bg-neutral-50 px-4 py-2"
        />
      )}

      {(session || onSessionCreated) && (
        <div className="shrink-0 space-y-2 border-t border-neutral-200 bg-neutral-50 px-4 py-3 pb-safe">
          <LearnAgentActions
            onAsk={(message) => void deferred.send(message)}
            disabled={streaming.isStreaming || deferred.isPending}
          />
          <ChatInput
            onSend={(content) => void deferred.send(content)}
            isPending={streaming.isStreaming || deferred.isPending}
            placeholder={t('learn.board.chatPlaceholder')}
          />
        </div>
      )}
    </div>
  )
}
